import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Debug } from "components/global/DebugModal";
import LanguagePicker from "components/languagePicker";
import { useDoorAndLockState } from "hooks/useDoorAndLockState";
import { useNonRfidStage } from "hooks/useNonRfidStage";
import { useOrder } from "hooks/useOrder";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import type { Ref } from "react";
import { selectIsReportingIssue } from "slices/appStateSlice";
import { selectFinishedLocalRefillId } from "slices/transactionSlice";
import { DoorState, LockState } from "types";
import { REFILL_SUMMARY_HOLD_SECONDS, REFILL_SUMMARY_TIMEOUT_SECONDS } from "utils/designTokens";
import { isNonRfidFlow } from "utils/nonRfidFlow";
import { AutolockTimer, BackToHomeButton, DrawerOrderSummaryRow, TutorialModal } from "./components";
import type { BackToHomeButtonHandle } from "./components/BackToHomeButton";

type DrawerActionBarProps = {
	currentOrderStep: number;
	ref?: Ref<HTMLDivElement>;
	// Lets OrderProcessDrawer restart the refill summary's countdown when the summary is touched.
	summaryCountdownRef?: Ref<BackToHomeButtonHandle>;
};

// Non-RFID prototype: how long the "finish in the app" handoff stays up before the Terminal goes home.
const NON_RFID_HANDOFF_SECONDS = 20;

/**
 * App action bar, that handles displays basic information about user and order process
 */
export const DrawerActionBar = ({ currentOrderStep, ref, summaryCountdownRef }: DrawerActionBarProps) => {
	const { lockTimeout, doors, lock } = useDoorAndLockState();
	const { finishOrder } = useOrder();
	const isReportingIssue = useAppSelector(selectIsReportingIssue);
	const finishedLocalRefillId = useAppSelector(selectFinishedLocalRefillId);
	// The prototype playground (yarn proto) shows the app as customers see it, without the debug entry.
	const isDebug = !["production", "proto"].includes(import.meta.env.MODE);
	const handleFinishOrder = () => {
		finishOrder();
	};

	// Suspend the autolock countdown while ReportIssueModal is open so its onTimeout
	// can't dispatch finishOrder and unmount the modal mid-interaction.
	const doorsClosedAndUnlocked =
		lockTimeout && doors === DoorState.Closed && lock === LockState.Unlocked && !isReportingIssue;

	// Non-RFID prototype: no price row (there are no inventory frames) and a longer back-to-home
	// countdown during the handoff. It is rendered in the same slot for steps 3 and 5 so the FINISHED
	// frame arriving mid-countdown does not restart it.
	const isNonRfid = isNonRfidFlow();
	const nonRfidStage = useNonRfidStage();
	const showBackToHome = isNonRfid
		? nonRfidStage === "finish" && !isReportingIssue
		: currentOrderStep === 5 && !isReportingIssue;
	let backToHomeSeconds: number | undefined;
	if (isNonRfid) {
		backToHomeSeconds = NON_RFID_HANDOFF_SECONDS;
	} else if (finishedLocalRefillId) {
		backToHomeSeconds = REFILL_SUMMARY_TIMEOUT_SECONDS;
	}
	// Only the refill summary waits while someone is using it — not an order, not the non-RFID handoff.
	const holdsOnTouch = !isNonRfid && finishedLocalRefillId !== null;

	return (
		<ActionBar
			ref={ref}
			className="actionBar"
			sx={{
				width: currentOrderStep === 1 ? "330px" : "100vw",
			}}
			data-testid="action-bar"
		>
			<LanguagePicker />
			<TutorialModal />
			{isDebug && <Debug />}
			{doorsClosedAndUnlocked && <AutolockTimer lockTimeout={lockTimeout} onTimeout={handleFinishOrder} />}
			{!isNonRfid && <DrawerOrderSummaryRow orderStep={currentOrderStep} />}
			{/* Keyed by the finished refill: the 60 s start when the summary appears, even if step 5 began earlier. */}
			{showBackToHome && (
				<BackToHomeButton
					key={finishedLocalRefillId ?? "order"}
					finishOrder={() => handleFinishOrder()}
					seconds={backToHomeSeconds}
					labelKey={finishedLocalRefillId ? "order.refillSummary.confirm" : undefined}
					holdSeconds={holdsOnTouch ? REFILL_SUMMARY_HOLD_SECONDS : undefined}
					ref={holdsOnTouch ? summaryCountdownRef : undefined}
				/>
			)}
		</ActionBar>
	);
};

const ActionBar = styled(Box)(({ theme }) => ({
	position: "fixed",
	bottom: 0,
	left: 0,
	right: 0,
	padding: "40px ",
	display: "flex",
	zIndex: 1201,
	gap: theme.spacing(2),
	alignItems: "center",
}));

DrawerActionBar.displayName = "DrawerActionBar";
